// src/pages/PickingPage.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import SkuBarcodeModal, { normalizeSkuForBarcode } from '../components/SkuBarcodeModal';
import './PickingPage.css';
import { getToken, getAuthHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { getOrderAssignment } from '../utils/orderMetrics';
import {
  choosePreferredPickingOrder,
  getPickingOrderId,
  readActivePickingOrderId,
  readPickingQueueContext,
  saveActivePickingOrder,
  savePickingQueueContext,
} from '../utils/pickingNavigation';

const normalizeUser = (value) => {
  if (!value) return '';
  return String(value).trim().toLowerCase();
};

const ConfirmModal = ({
  open,
  title = 'Confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel
}) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h3 className="modal-title">{title}</h3>
        {message && <p className="modal-message">{message}</p>}
        <div className="modal-actions">
          <button className="modal-btn cancel" onClick={onCancel}>{cancelLabel}</button>
          <button className="modal-btn confirm" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const playScannerTone = (type = 'success') => {
  if (typeof window === 'undefined') return;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  try {
    const context = new AudioContext();
    const playBeep = (frequency, startTime, duration, volume = 0.08) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, startTime);
      gain.gain.setValueAtTime(0.0001, startTime);
      gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration + 0.02);
    };

    const now = context.currentTime;
    if (type === 'success') {
      playBeep(880, now, 0.09);
      playBeep(1175, now + 0.1, 0.11);
    } else {
      playBeep(220, now, 0.16, 0.09);
      playBeep(165, now + 0.18, 0.18, 0.09);
    }

    setTimeout(() => context.close().catch(() => {}), 600);
  } catch (err) {
    console.warn('Scanner tone failed:', err);
  }
};

const storeKeyFor = (orderId, itemId) => `${orderId || ''}-${itemId || ''}`;
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/60';
const PRODUCT_META_CACHE_KEY = 'productMetaBySku';
const PRODUCT_META_CACHE_TTL = 4 * 60 * 60 * 1000;
const PRODUCT_META_CACHE_LIMIT = 500;
const productMetaRequests = new Map();

const extractBaseSku = (value) => {
  if (!value) return '';
  const str = String(value).trim();
  const idx = str.indexOf('-');
  if (idx <= 0) return str;
  const suffix = str.slice(idx + 1);
  if (/^\d+$/.test(suffix)) {
    // Sufixo só numérico: mantém SKU completo
    return str;
  }
  return str.slice(0, idx);
};

const getSkuLookupCandidates = (value) => {
  if (!value) return [];
  const sku = String(value).trim();
  if (!sku) return [];
  const idx = sku.indexOf('-');
  if (idx <= 0) return [sku];
  const suffix = sku.slice(idx + 1);
  const baseSku = sku.slice(0, idx);
  if (/^c$/i.test(suffix)) return [sku, baseSku];
  const baseCandidate = extractBaseSku(sku);
  return baseCandidate && baseCandidate !== sku ? [baseCandidate, sku] : [sku];
};

const emptyProductMeta = () => ({
  image: PLACEHOLDER_IMAGE,
  location: '',
  locationCashCarry: '',
  zone: '',
  lane: '',
  barcode: '',
  hasImage: false,
});

const readProductMetaCache = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PRODUCT_META_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const fresh = {};
    Object.entries(parsed || {}).forEach(([sku, entry]) => {
      if (entry?.savedAt && now - entry.savedAt < PRODUCT_META_CACHE_TTL && entry.meta) {
        fresh[sku] = entry;
      }
    });
    return fresh;
  } catch {
    return {};
  }
};

const writeProductMetaCache = (cache) => {
  if (typeof window === 'undefined') return;
  try {
    const entries = Object.entries(cache || {})
      .sort((a, b) => Number(b[1]?.savedAt || 0) - Number(a[1]?.savedAt || 0))
      .slice(0, PRODUCT_META_CACHE_LIMIT);
    window.localStorage.setItem(PRODUCT_META_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch (err) {
    console.warn('Failed to persist product meta cache:', err);
  }
};

const getCachedProductMeta = (sku) => {
  const key = String(sku || '').trim();
  if (!key) return null;
  return readProductMetaCache()[key]?.meta || null;
};

const setCachedProductMeta = (sku, meta) => {
  const key = String(sku || '').trim();
  if (!key || !meta) return;
  const cache = readProductMetaCache();
  cache[key] = { savedAt: Date.now(), meta };
  writeProductMetaCache(cache);
};

const collator = typeof Intl !== 'undefined'
  ? new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
  : null;

const compareText = (a = '', b = '') => {
  const left = String(a);
  const right = String(b);
  if (collator) return collator.compare(left, right);
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();
const includesAny = (haystack, keywords = []) => {
  if (!haystack) return false;
  return keywords.some((kw) => haystack.includes(kw));
};

const isNumericOnly = (value) => /^\d+$/.test(value || '');

const getLaneText = (item) => normalizeText(item?.extension_attributes?.lane);
const getZoneText = (item) => {
  const z = normalizeText(item?.extension_attributes?.zone);
  if (isNumericOnly(z)) return ''; // ignore pure numbers for ordering
  return z;
};

  const getCategoryInfo = (item) => {
    const lane = getLaneText(item);
    const zone = getZoneText(item);
  const name = normalizeText(item?.name);
  const qtyOrdered = Number(item?.qty_ordered) || 0;
  const combined = `${lane} ${zone}`.trim();

  const frozenHit =
    includesAny(lane, ['frozen', 'freezer']) ||
    includesAny(zone, ['frozen', 'freezer']) ||
    includesAny(name, ['frozen', 'freezer']);

  const wholesaleHit =
    includesAny(lane, ['wholesale', 'warehouse']) ||
    includesAny(zone, ['wholesale', 'warehouse']) ||
    /\[case\]/i.test(String(item?.name || '')) ||
    qtyOrdered >= 12;

  if (wholesaleHit) {
    return { key: 'wholesale', priority: 25, subPriority: 0, label: 'Warehouse' };
  }

  if (frozenHit) {
    return { key: 'frozen', priority: 24, subPriority: 0, label: 'Frozen' };
  }

  const isEcom = includesAny(combined, [
    'ecom',
    'area 1',
    'area1',
    'area 2',
    'area2',
    'ecom 1',
    'ecom1',
    'ecom 2',
    'ecom2',
  ]);
  if (isEcom) {
    const area1 = includesAny(combined, ['area 1', 'area1', 'ecom 1', 'ecom1']);
    const area2 = includesAny(combined, ['area 2', 'area2', 'ecom 2', 'ecom2']);
    const subPriority = area1 ? 1 : area2 ? 2 : 3;
    return { key: 'ecom', priority: 1, subPriority, label: 'Ecom' };
  }

  const CATEGORY_RULES = [
    { key: 'rice', priority: 2, keywords: ['rice'], label: 'Rice' },
    { key: 'noodles', priority: 3, keywords: ['noodles', 'noodle'], label: 'Noodles' },
    { key: 'europe', priority: 4, keywords: ['europe', 'european'], label: 'Europe' },
    { key: 'nestle', priority: 5, keywords: ['nestle'], label: 'Nestle' },
    { key: 'flour', priority: 6, keywords: ['flour'], label: 'Flour' },
    { key: 'viet', priority: 8, keywords: ['viet', 'vietnam', 'vietnan', 'vietnamese'], label: 'Vietnam' },
    { key: 'malaysian', priority: 9, keywords: ['malaysian', 'malaysia', 'malaisia', 'malay'], label: 'Malaysian' },
    { key: 'thai', priority: 10, keywords: ['thai', 'thailand', 'thailander'], label: 'Thai' },
    { key: 'sauces', priority: 11, keywords: ['sauces', 'sauce'], label: 'Sauces' },
    { key: 'hotpot', priority: 12, keywords: ['hotpot', 'hot pot'], label: 'Hotpot' },
    { key: 'philippines', priority: 13, keywords: ['philippine', 'philippines', 'filipino', 'filipina'], label: 'Philippines' },
    { key: 'front-store', priority: 14, keywords: ['front store', 'front'], label: 'Front Store' },
    { key: 'drinks', priority: 15, keywords: ['drinks', 'drink'], label: 'Drinks' },
    { key: 'fresh', priority: 16, keywords: ['fresh', 'fridge', 'chill'], label: 'Fresh / Fridge / Chill' },
    { key: 'japanese', priority: 17, keywords: ['japanese', 'japan'], label: 'Japanese' },
    { key: 'korean', priority: 18, keywords: ['korean', 'korea'], label: 'Korean' },
    { key: 'china', priority: 19, keywords: ['china', 'chinese'], label: 'China' },
    { key: 'coffee-tea', priority: 20, keywords: ['coffee and tea', 'tea and coffee', 'coffee', 'tea'], label: 'Coffee and Tea' },
    { key: 'snacks', priority: 21, keywords: ['snacks', 'snack'], label: 'Snacks' },
    { key: 'homeware', priority: 22, keywords: ['homeware'], label: 'Homeware' },
  ];

  for (const rule of CATEGORY_RULES) {
    if (includesAny(lane, rule.keywords) || includesAny(zone, rule.keywords)) {
      return { key: rule.key, priority: rule.priority, subPriority: 0, label: rule.label };
    }
  }

  const indiaKeywords = ['india', 'indian', 'patak', "patak's", 'pataks', 'khanum', 'east end', 'heera'];
  if (includesAny(lane, indiaKeywords) || includesAny(zone, indiaKeywords)) {
    let subPriority = 5;
    if (includesAny(combined, ['patak', "patak's", 'pataks'])) subPriority = 1;
    else if (includesAny(combined, ['khanum'])) subPriority = 2;
    else if (includesAny(combined, ['east end'])) subPriority = 3;
    else if (includesAny(combined, ['heera'])) subPriority = 4;
    return { key: 'india', priority: 7, subPriority, label: 'India' };
  }

  if (includesAny(lane, ['alcohol']) || includesAny(zone, ['alcohol'])) {
    return { key: 'alcohol', priority: 23, subPriority: 0, label: 'Alcohol' };
  }

  // Nome contém "fresh" mas não bateu outra categoria
  if (includesAny(name, ['fresh'])) {
    return { key: 'fresh', priority: 16, subPriority: 0, label: 'Fresh / Fridge / Chill' };
  }

  return { key: 'other', priority: 0, subPriority: 0, label: 'Other' };
};

const isFrozenItem = (item) => getCategoryInfo(item).key === 'frozen';
const isWholesaleItem = (item) => getCategoryInfo(item).key === 'wholesale';

const sortOrderItems = (items = []) => {
  const sorted = [...items];
  sorted.sort((a, b) => {
    const aCat = getCategoryInfo(a);
    const bCat = getCategoryInfo(b);

    if (aCat.priority !== bCat.priority) {
      return aCat.priority - bCat.priority;
    }
    if (aCat.subPriority !== bCat.subPriority) {
      return aCat.subPriority - bCat.subPriority;
    }

    const laneDiff = compareText(getLaneText(a), getLaneText(b));
    if (laneDiff !== 0) return laneDiff;

    const zoneDiff = compareText(getZoneText(a), getZoneText(b));
    if (zoneDiff !== 0) return zoneDiff;

    return compareText(a.name || '', b.name || '');
  });
  return sorted;
};

const PICKING_CACHE_KEY = 'pickingPageState';
const PICKING_CACHE_TTL = 5 * 60 * 1000;
const getOrderKey = (order) => {
  if (!order) return '';
  const inc = order.increment_id !== undefined && order.increment_id !== null ? String(order.increment_id) : '';
  const ent = order.entity_id !== undefined && order.entity_id !== null ? String(order.entity_id) : '';
  return inc || ent || '';
};

const readPickingPageCache = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PICKING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.orders) || !parsed.orders.length) return null;
    if (parsed.timestamp && Date.now() - Number(parsed.timestamp) > PICKING_CACHE_TTL) {
      window.sessionStorage.removeItem(PICKING_CACHE_KEY);
      return null;
    }
    return {
      orders: parsed.orders,
      currentIndex: Number.isFinite(parsed.currentIndex) ? parsed.currentIndex : 0,
      isOverrideView: Boolean(parsed.isOverrideView),
      currentOrderId: String(parsed.currentOrderId || ''),
    };
  } catch (err) {
    console.warn('Failed to read picking cache:', err);
    try {
      window.sessionStorage.removeItem(PICKING_CACHE_KEY);
    } catch {
      // ignore
    }
    return null;
  }
};

const persistPickingPageCache = (payload) => {
  if (typeof window === 'undefined') return;
  try {
    const snapshot = {
      ...payload,
      timestamp: Date.now(),
    };
    window.sessionStorage.setItem(PICKING_CACHE_KEY, JSON.stringify(snapshot));
  } catch (err) {
    console.warn('Failed to persist picking cache:', err);
  }
};

const clearPickingPageCache = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PICKING_CACHE_KEY);
  } catch {
    // ignore
  }
};

const PickingPage = () => {
  const pickingCacheRef = useRef(null);
  if (pickingCacheRef.current === null) {
    pickingCacheRef.current = readPickingPageCache();
  }
  const cachedPickingState = pickingCacheRef.current;
  const cachedOrders = Array.isArray(cachedPickingState?.orders) ? cachedPickingState.orders : [];
  const hasCachedOrders = cachedOrders.length > 0;

  const [orders, setOrders] = useState(() => cachedOrders);
  const [currentIndex, setCurrentIndex] = useState(() => {
    const idx = Number(cachedPickingState?.currentIndex);
    if (Number.isFinite(idx) && idx >= 0) return idx;
    return 0;
  });
  const [pickedItems, setPickedItems] = useState({});
  const [isLoading, setIsLoading] = useState(!hasCachedOrders);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showResolveAlert, setShowResolveAlert] = useState(false);
  const [releaseSelection, setReleaseSelection] = useState([]);
  const [releaseMode, setReleaseMode] = useState(false);
  const loadRevealedLocations = () => {
    try {
      const raw = localStorage.getItem('revealedLocationsByOrder');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };
  const [revealedLocations, setRevealedLocations] = useState(loadRevealedLocations);
  const [storeEditing, setStoreEditing] = useState({});
  const [storeSavingKey, setStoreSavingKey] = useState('');
  const holdTimersRef = useRef({});
  const ordersRef = useRef(orders);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const persistRevealedLocations = useCallback((next) => {
    try {
      localStorage.setItem('revealedLocationsByOrder', JSON.stringify(next));
    } catch (err) {
      console.warn('Failed to persist revealed locations:', err);
    }
  }, []);

  const toCatalogUrl = useCallback((filePath) => {
    if (!filePath) return null;
    const raw = String(filePath).trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    const normalized = raw.startsWith('/') ? raw : `/${raw}`;
    return `/media/catalog/product${normalized}`;
  }, []);

  const fetchProductMeta = useCallback(async (sku, fallbackSku) => {
    const candidates = getSkuLookupCandidates(sku);
    if (fallbackSku && !candidates.includes(fallbackSku)) candidates.push(fallbackSku);
    const requestKey = candidates.join('|');
    if (productMetaRequests.has(requestKey)) return productMetaRequests.get(requestKey);

    const request = (async () => {
    try {
      let fallbackMeta = null;

      for (const candidate of candidates) {
        try {
          const cached = getCachedProductMeta(candidate);
          if (cached) {
            if (cached.hasImage) return cached;
            if (!fallbackMeta) fallbackMeta = cached;
            continue;
          }

          const response = await apiFetch(`/rest/V1/products/${encodeURIComponent(candidate)}`, {
            headers: getAuthHeaders(),
          });
          if (!response.ok) continue;
          const data = await response.json();
          const galleryEntry = Array.isArray(data?.media_gallery_entries) ? data.media_gallery_entries[0] : null;

          const customAttrs = Array.isArray(data?.custom_attributes) ? data.custom_attributes : [];
          const attrMap = customAttrs.reduce((acc, attr) => {
            if (attr?.attribute_code) acc[attr.attribute_code] = attr.value;
            return acc;
          }, {});

          const attrImagePath = attrMap.image || attrMap.small_image || attrMap.thumbnail;
          const resolvedImage = toCatalogUrl(attrImagePath || galleryEntry?.file);
          const image = resolvedImage || PLACEHOLDER_IMAGE;
          const barcode = data?.barcode || attrMap.barcode || attrMap.ean || attrMap.gtin || attrMap.upc || '';

          const meta = {
            image,
            location: '',
            locationCashCarry: '',
            zone: attrMap.zone || '',
            lane: attrMap.lane || '',
            barcode,
            hasImage: Boolean(resolvedImage),
          };
          setCachedProductMeta(candidate, meta);
          if (resolvedImage) return meta;
          if (!fallbackMeta) fallbackMeta = meta;
        } catch (inner) {
          // try next candidate
        }
      }

      return fallbackMeta || emptyProductMeta();
    } catch (error) {
      console.error(`Error fetching product meta ${sku}:`, error);
      return emptyProductMeta();
    }
    })();

    productMetaRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      productMetaRequests.delete(requestKey);
    }
  }, [toCatalogUrl]);

  const applyProductMetaToItem = useCallback((item, meta = emptyProductMeta()) => ({
    ...item,
    image_url: meta.image || item.image_url || PLACEHOLDER_IMAGE,
    extension_attributes: {
      ...item.extension_attributes,
      location: item.extension_attributes?.location || meta.location || '',
      zone: item.extension_attributes?.zone || meta.zone || '',
      lane: item.extension_attributes?.lane || meta.lane || '',
      barcode: item.extension_attributes?.barcode || meta.barcode || '',
    },
  }), []);

  const openStoreEditing = (key, value = '') => {
    setStoreEditing((prev) => ({
      ...prev,
      [key]: value || '',
    }));
  };

  const closeStoreEditing = (key) => {
    setStoreEditing((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleStoreInputChange = (key, value) => {
    setStoreEditing((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const startHoldTimer = (key, value) => {
    holdTimersRef.current[key] = setTimeout(() => {
      openStoreEditing(key, value);
      delete holdTimersRef.current[key];
    }, 5000);
  };

  const cancelHoldTimer = (key) => {
    const timer = holdTimersRef.current[key];
    if (timer) {
      clearTimeout(timer);
      delete holdTimersRef.current[key];
    }
  };

  const saveStoreLocation = useCallback(async (item, newValue) => {
    if (!item?.sku) throw new Error('Missing product SKU for this item.');
    const payload = {
      product: {
        sku: item.sku,
        custom_attributes: [
          {
            attribute_code: 'lane',
            value: newValue,
          },
        ],
      },
    };
    const res = await apiFetch(`/rest/V1/products/${encodeURIComponent(item.sku)}`, {
      method: 'PUT',
      headers: getAuthHeaders(true),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.text();
      throw new Error(body || `Failed to update store location (${res.status})`);
    }
    return res.json();
  }, []);

  const applyStoreLocationUpdate = useCallback((orderId, itemId, value) => {
    setOrders((prev) =>
      prev.map((order) => {
        if (String(order.increment_id) !== String(orderId)) return order;
        const updatedItems = (order.items || []).map((it) =>
          it.item_id === itemId
            ? {
                ...it,
                extension_attributes: {
                  ...it.extension_attributes,
                  lane: value,
                },
              }
            : it
        );
        return {
          ...order,
          items: sortOrderItems(updatedItems),
        };
      })
    );
  }, []);

  const handleStoreSave = async (order, item, key) => {
    const value = (storeEditing[key] || '').trim();
    if (!value) {
      alert('Please enter a lane value.');
      return;
    }
    try {
      setStoreSavingKey(key);
      await saveStoreLocation(item, value);
      const cached = getCachedProductMeta(item.sku) || emptyProductMeta();
      setCachedProductMeta(item.sku, {
        ...cached,
        image: item.image_url || cached.image || PLACEHOLDER_IMAGE,
        lane: value,
      });
      applyStoreLocationUpdate(order.increment_id, item.item_id, value);
      closeStoreEditing(key);
      alert('Lane updated successfully.');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to update lane.');
    } finally {
      setStoreSavingKey('');
    }
  };

  const [isOverrideView, setIsOverrideView] = useState(() => Boolean(cachedPickingState?.isOverrideView));

  useEffect(() => {
    return () => {
      Object.values(holdTimersRef.current).forEach((timer) => clearTimeout(timer));
      if (skuHoldTimerRef.current) clearTimeout(skuHoldTimerRef.current);
    };
  }, []);

  // Inline edit qty controls
  const [editing, setEditing] = useState(null); // { orderId, itemId, maxQty, value }
  const [editedQty, setEditedQty] = useState({}); // { [orderId]: { [itemId]: number } }
  const [openActionMenu, setOpenActionMenu] = useState(null); // { orderId, itemId }
  const [skuCodeValue, setSkuCodeValue] = useState('');
  const scannerBufferRef = useRef('');
  const scannerTimerRef = useRef(null);
  const skuHoldTimerRef = useRef(null);

  useEffect(() => {
    if (!orders.length) {
      clearPickingPageCache();
      return;
    }
    const safeIndex = Math.min(Math.max(currentIndex, 0), orders.length - 1);
    persistPickingPageCache({
      orders,
      currentIndex: safeIndex,
      isOverrideView,
      currentOrderId: getPickingOrderId(orders[safeIndex]),
    });
  }, [orders, currentIndex, isOverrideView]);

  useEffect(() => {
    if (!orders.length) {
      setCurrentIndex(0);
      return;
    }
    setCurrentIndex((prev) => {
      if (prev < orders.length) return prev;
      return Math.max(orders.length - 1, 0);
    });
  }, [orders.length]);

  // Rehydrate local selections so FinalizePage can read
  useEffect(() => {
    try {
      const savedPicked = localStorage.getItem('pickedSelections');
      if (savedPicked) setPickedItems(JSON.parse(savedPicked));
    } catch (e) {
      console.warn('Failed to read pickedSelections from localStorage:', e);
    }

    try {
      const savedEdited = localStorage.getItem('editedQtyByOrder');
      if (savedEdited) setEditedQty(JSON.parse(savedEdited));
    } catch (e) {
      console.warn('Failed to read editedQtyByOrder from localStorage:', e);
    }
  }, []);

  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [releaseProgress, setReleaseProgress] = useState({ current: 0, total: 0 });

  // Params (supports /picking/:incrementId or /picking/:orderId)
  const params = useParams();
  const navigate = useNavigate();
  const preferredIncId = params?.incrementId || params?.orderId || null;

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location?.pathname) {
      localStorage.setItem('lastPickingPath', window.location.pathname);
    }
  }, [preferredIncId]);

  // Current user saved at login (prefer 'operator', fallback to 'username')
  const storedUsername = localStorage.getItem('operator') || localStorage.getItem('username') || '';
  const username = storedUsername.trim();
  const usernameKey = normalizeUser(storedUsername);

  const toggleActionMenu = (orderId, itemId) => {
    setOpenActionMenu((prev) => {
      if (prev && prev.orderId === orderId && prev.itemId === itemId) {
        return null;
      }
      return { orderId, itemId };
    });
  };

  const closeActionMenu = () => setOpenActionMenu(null);

  const enableReleaseMode = () => {
    const currentKey = getOrderKey(currentOrder);
    setReleaseMode(true);
    setReleaseSelection((prev) => {
      if (prev.length) return prev;
      return currentKey ? [currentKey] : [];
    });
  };

  const cancelReleaseMode = () => {
    setReleaseMode(false);
    setReleaseSelection([]);
  };

  useEffect(() => {
    let cancelled = false;

    const enrichOrdersWithProductMeta = async (ordersToEnrich) => {
      const enriched = await Promise.all((ordersToEnrich || []).map(async (detail) => {
        const itemsById = (detail.items || []).reduce((acc, it) => {
          acc[it.item_id] = it;
          return acc;
        }, {});

        const itemsWithMeta = await Promise.all((detail.items || []).map(async (item) => {
          const parent = item.parent_item_id ? itemsById[item.parent_item_id] : null;
          const parentSku = parent?.sku;
          const meta = await fetchProductMeta(item.sku, parentSku);
          return applyProductMetaToItem(item, meta);
        }));

        return { ...detail, items: sortOrderItems(itemsWithMeta) };
      }));

      if (!cancelled) setOrders(enriched);
    };

    const fetchOrders = async () => {
      const hasExistingOrders = ordersRef.current.length > 0;
      if (hasExistingOrders) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      try {
        const overrideIncrementId = localStorage.getItem('operatorOverrideIncrementId');
        const token = await getToken();
        console.log('Token:', token);

        // List orders with status = picking
        const response = await apiFetch(
          `/rest/V1/orders?searchCriteria[filter_groups][0][filters][0][field]=status&searchCriteria[filter_groups][0][filters][0][value]=picking&searchCriteria[filter_groups][0][filters][0][condition_type]=eq`,
          {
            method: 'GET',
            headers: getAuthHeaders(),
          }
        );

        const data = await response.json();
        console.log('Orders fetched:', data);

        const ordersDetailed = await Promise.all((data.items || []).map(async (order) => {
          // Full order (status_histories, complete items, etc.)
          const detailRes = await apiFetch(`/rest/V1/orders/${order.entity_id}`, {
            headers: getAuthHeaders(),
          });
          const detail = await detailRes.json();
          console.log('Order detail for', order.increment_id, ':', detail);

          // If you want to filter by assignment in comments, re-enable this:
          // const assigned = (detail.status_histories || []).some(h => (h.comment || '').includes(`Assigned to ${username}`));
          // if (!assigned) return null;

          const isProcessingStatus = String(detail.status).toLowerCase() === 'processing';
          const assignedTo = isProcessingStatus ? null : getOrderAssignment(detail).currentOperator;
          const assignedKey = normalizeUser(assignedTo);
          const isOverride = overrideIncrementId && detail.increment_id === overrideIncrementId;
          if (!isProcessingStatus && assignedKey && assignedKey !== usernameKey && !isOverride) {
            return null;
          }

          const items = (detail.items || []).map((item) => ({
            ...item,
            image_url: item.image_url || PLACEHOLDER_IMAGE,
          }));

          return { ...detail, items: sortOrderItems(items) };
        }));

        const filtered = ordersDetailed.filter(Boolean);
        console.log('Final filtered orders:', filtered);

        const activeOrderId =
          getPickingOrderId(ordersRef.current[currentIndexRef.current]) ||
          cachedPickingState?.currentOrderId ||
          readActivePickingOrderId();
        const routePreferredId = hasExistingOrders && activeOrderId ? '' : preferredIncId;

        let finalList;
        if (overrideIncrementId) {
          const overrideList = filtered.filter((order) => order.increment_id === overrideIncrementId);
          if (overrideList.length) {
            finalList = overrideList;
            setIsOverrideView(true);
          } else {
            finalList = filtered;
            setIsOverrideView(false);
          }
        } else {
          finalList = filtered;
          setIsOverrideView(false);
        }

        if (cancelled) return;
        setOrders(finalList);
        enrichOrdersWithProductMeta(finalList).catch((err) => {
          console.warn('Product metadata enrichment failed:', err);
        });

        if (overrideIncrementId) {
          localStorage.removeItem('operatorOverrideIncrementId');
        }

        const targetId = overrideIncrementId
          ? getPickingOrderId(finalList[0])
          : choosePreferredPickingOrder(finalList, {
              preferredId: routePreferredId,
              activeId: activeOrderId,
              context: readPickingQueueContext(),
            });

        setCurrentIndex((prev) => {
          if (!finalList.length) return 0;
          const targetIndex = targetId
            ? finalList.findIndex((order) => getPickingOrderId(order) === targetId)
            : -1;
          if (targetIndex >= 0) return targetIndex;
          return Math.min(Math.max(prev, 0), finalList.length - 1);
        });
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        if (!cancelled) {
          setIsRefreshing(false);
          setIsLoading(false);
        }
      }
    };

    fetchOrders();
    return () => {
      cancelled = true;
    };
  }, [preferredIncId]);

  useEffect(() => {
    // drop selections that no longer exist
    setReleaseSelection((prev) => {
      const valid = new Set(orders.map((o) => getOrderKey(o)).filter(Boolean));
      return prev.filter((id) => valid.has(id));
    });
  }, [orders]);

  const toggleItemStatus = (orderId, itemId, status) => {
    setPickedItems((prev) => {
      const current = prev[orderId]?.[itemId];
      const currentStatus = typeof current === 'string' ? current : current?.status;
      const isSame = currentStatus === status;

      const nextOrderMap = { ...(prev[orderId] || {}) };

      if (isSame) {
        delete nextOrderMap[itemId]; // unmark
      } else {
        nextOrderMap[itemId] = status; // apply new status
      }

      return {
        ...prev,
        [orderId]: nextOrderMap,
      };
    });
    closeActionMenu();
  };

  const toKey = (value) => {
    if (value === undefined || value === null) return '';
    return String(value);
  };

  const getItemStatus = (orderId, itemId) => {
    if (!orderId && orderId !== 0) return '';
    const orderMap = pickedItems[orderId] || {};
    const entry = orderMap[itemId];
    if (!entry) return '';
    if (typeof entry === 'string') return entry;
    if (entry && typeof entry === 'object' && entry.status) return entry.status;
    return '';
  };

  const normalizeEditEntry = (entry, maxQty) => {
    const clamp = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return 0;
      if (num < 0) return 0;
      if (num > maxQty) return maxQty;
      return num;
    };

    if (entry && typeof entry === 'object') {
      if (entry.removedQty !== undefined || entry.removed !== undefined || entry.diff !== undefined) {
        const removed = clamp(entry.removedQty ?? entry.removed ?? entry.diff);
        return { keepQty: Math.max(maxQty - removed, 0), removedQty: removed };
      }
      if (entry.keepQty !== undefined || entry.keep !== undefined || entry.qty !== undefined) {
        const keep = clamp(entry.keepQty ?? entry.keep ?? entry.qty);
        return { keepQty: keep, removedQty: Math.max(maxQty - keep, 0) };
      }
    }

    if (entry !== undefined) {
      const keep = clamp(entry);
      return { keepQty: keep, removedQty: Math.max(maxQty - keep, 0) };
    }

    return { keepQty: maxQty, removedQty: 0 };
  };

  const getEditSnapshot = (orderId, itemId, maxQty) => {
    const orderKey = toKey(orderId);
    const itemKey = toKey(itemId);
    const orderMap = editedQty[orderKey] || {};
    if (Object.prototype.hasOwnProperty.call(orderMap, itemKey)) {
      return normalizeEditEntry(orderMap[itemKey], maxQty);
    }
    return { keepQty: maxQty, removedQty: 0 };
  };

  const startEdit = (orderId, itemId, maxQty) => {
    closeActionMenu();
    const safeMax = Number.isFinite(maxQty) && maxQty > 0 ? maxQty : 0;
    const { keepQty } = getEditSnapshot(orderId, itemId, safeMax);
    setEditing({ orderId, itemId, maxQty: safeMax, value: keepQty });
  };

  const cancelEdit = () => setEditing(null);

  const handleFoundClick = (orderId, itemId, orderedQty, isFound) => {
    closeActionMenu();
    if (isFound) {
      toggleItemStatus(orderId, itemId, 'found');
      return;
    }

    const safeQty = Number(orderedQty);
    if (Number.isFinite(safeQty) && safeQty > 1) {
      startEdit(orderId, itemId, safeQty);
      return;
    }

    toggleItemStatus(orderId, itemId, 'found');
  };

  const saveEdit = (orderId, itemId) => {
    if (!editing) return;
    const orderKey = toKey(orderId);
    const itemKey = toKey(itemId);
    const baseQty = Number(editing.maxQty) || 0;
    const desiredQty = Number(editing.value);
    const clampedDesired = Number.isFinite(desiredQty)
      ? Math.min(Math.max(desiredQty, 0), baseQty)
      : baseQty;
    const removedQty = Math.max(baseQty - clampedDesired, 0);

    setEditedQty((prev) => {
      const prevOrder = { ...(prev[orderKey] || {}) };

      if (removedQty <= 0) {
        if (!Object.prototype.hasOwnProperty.call(prevOrder, itemKey)) {
          return prev;
        }
        delete prevOrder[itemKey];
      } else {
        prevOrder[itemKey] = {
          keepQty: clampedDesired,
          removedQty,
        };
      }

      const next = { ...prev };
      if (Object.keys(prevOrder).length === 0) {
        delete next[orderKey];
      } else {
        next[orderKey] = prevOrder;
      }
      return next;
    });

    setPickedItems((prev) => {
      if (removedQty >= baseQty) return prev; // nothing left to pick, operator should mark as removed separately
      const prevOrder = prev[orderKey] || {};
      const current = prevOrder[itemKey];
      const currentStatus = typeof current === 'string' ? current : current?.status;
      if (currentStatus === 'found') return prev;
      return {
        ...prev,
        [orderKey]: {
          ...prevOrder,
          [itemKey]: 'found',
        },
      };
    });
    setEditing(null);
  };

  // Persist selections & edited qty so other pages (FinalizePage) can consume
  useEffect(() => {
    try {
      localStorage.setItem('pickedSelections', JSON.stringify(pickedItems));
    } catch (e) {
      console.warn('Failed to save pickedSelections to localStorage:', e);
    }
  }, [pickedItems]);

  useEffect(() => {
    try {
      localStorage.setItem('editedQtyByOrder', JSON.stringify(editedQty));
    } catch (e) {
      console.warn('Failed to save editedQtyByOrder to localStorage:', e);
    }
  }, [editedQty]);

  const currentOrder = orders[currentIndex];

  useEffect(() => {
    setOpenActionMenu(null);
    if (currentOrder) {
      saveActivePickingOrder(currentOrder);
    }
  }, [currentOrder?.increment_id]);

  const getOrderProgress = (order) => {
    const items = order?.items || [];
    if (!items.length) return { resolved: 0, total: 0 };
    let resolved = 0;
    for (const item of items) {
      const status = getItemStatus(order.increment_id, item.item_id);
      if (status === 'found' || status === 'removed') {
        resolved += 1;
      }
    }
    return { resolved, total: items.length };
  };

  const handleSelectOrder = (index) => {
    setCurrentIndex(index);
    setShowResolveAlert(false);
  };

  const clearSkuHoldTimer = () => {
    if (skuHoldTimerRef.current) {
      clearTimeout(skuHoldTimerRef.current);
      skuHoldTimerRef.current = null;
    }
  };

  const startSkuHold = (sku) => {
    clearSkuHoldTimer();
    const value = normalizeSkuForBarcode(sku);
    if (!value) return;
    skuHoldTimerRef.current = setTimeout(() => {
      setSkuCodeValue(value);
      skuHoldTimerRef.current = null;
    }, 550);
  };

  const normalizeScanCode = (value) => String(value || '').trim().replace(/\s+/g, '').toLowerCase();

  const getItemBarcodeCandidates = (item = {}) => {
    const ext = item.extension_attributes || {};
    return [
      ext.barcode,
      ext.ean,
      ext.gtin,
      ext.upc,
      item.barcode,
      item.ean,
      item.gtin,
      item.upc,
    ]
      .map(normalizeScanCode)
      .filter(Boolean);
  };

  const findScannedItem = (rawCode) => {
    const code = normalizeScanCode(rawCode);
    if (!code) return null;
    const orderedOrders = currentOrder
      ? [currentOrder, ...orders.filter((order) => order.increment_id !== currentOrder.increment_id)]
      : orders;

    for (const order of orderedOrders) {
      const items = sortOrderItems(order?.items || []);
      const matchedItems = items.filter((item) => getItemBarcodeCandidates(item).includes(code));
      const unresolvedMatch = matchedItems.find((item) => {
        const status = getItemStatus(order.increment_id, item.item_id);
        return status !== 'found' && status !== 'removed';
      });
      const item = unresolvedMatch || matchedItems[0];
      if (item) return { order, item };
    }

    return null;
  };

  const processScannedCode = useCallback((rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return;

    const match = findScannedItem(code);
    if (!match) {
      playScannerTone('error');
      return;
    }

    const targetOrderId = match.order.increment_id;
    const targetStatus = getItemStatus(targetOrderId, match.item.item_id);
    if (targetStatus === 'found') {
      playScannerTone('error');
      return;
    }
    if (targetStatus === 'removed') {
      playScannerTone('error');
      return;
    }

    const targetIndex = orders.findIndex((order) => order.increment_id === targetOrderId);
    if (targetIndex >= 0 && targetIndex !== currentIndex) {
      setCurrentIndex(targetIndex);
    }

    const orderedQty = Number(match.item.qty_ordered) || 0;
    handleFoundClick(targetOrderId, match.item.item_id, orderedQty, false);
    playScannerTone('success');
  }, [currentIndex, currentOrder, orders, pickedItems]);

  useEffect(() => {
    const clearScannerBuffer = () => {
      scannerBufferRef.current = '';
      if (scannerTimerRef.current) {
        clearTimeout(scannerTimerRef.current);
        scannerTimerRef.current = null;
      }
    };

    const isEditableTarget = (target) => {
      if (!target) return false;
      if (target.id === 'barcodeScannerInput') return true;
      const tag = String(target.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
    };

    const handleGlobalScannerKey = (event) => {
      if (editing || isEditableTarget(event.target)) return;
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      if (event.key === 'Enter' || event.key === 'Tab') {
        const code = scannerBufferRef.current;
        clearScannerBuffer();
        if (code.length >= 4) {
          event.preventDefault();
          processScannedCode(code);
        }
        return;
      }

      if (event.key && event.key.length === 1) {
        scannerBufferRef.current += event.key;
        if (scannerTimerRef.current) clearTimeout(scannerTimerRef.current);
        scannerTimerRef.current = setTimeout(() => {
          const code = scannerBufferRef.current;
          clearScannerBuffer();
          if (code.length >= 4) processScannedCode(code);
        }, 220);
      }
    };

    window.addEventListener('keydown', handleGlobalScannerKey, true);
    return () => {
      window.removeEventListener('keydown', handleGlobalScannerKey, true);
      clearScannerBuffer();
    };
  }, [editing, processScannedCode]);

  const cleanupReleasedOrder = (orderKey) => {
    if (!orderKey) return;
    setPickedItems((prev) => {
      if (!prev[orderKey]) return prev;
      const next = { ...prev };
      delete next[orderKey];
      return next;
    });
    setEditedQty((prev) => {
      if (!prev[orderKey]) return prev;
      const next = { ...prev };
      delete next[orderKey];
      return next;
    });
    setRevealedLocations((prev) => {
      if (!prev[orderKey]) return prev;
      const next = { ...prev };
      delete next[orderKey];
      persistRevealedLocations(next);
      return next;
    });
    setStoreEditing((prev) => {
      const prefix = `${orderKey}-`;
      const next = {};
      let changed = false;
      Object.entries(prev).forEach(([key, value]) => {
        if (key.startsWith(prefix)) {
          changed = true;
          return;
        }
        next[key] = value;
      });
      return changed ? next : prev;
    });
    setReleaseSelection((prev) => prev.filter((id) => id !== orderKey));
  };

  const releaseSingleOrder = async (order) => {
    const releasedOrderKey = getOrderKey(order);
    if (!releasedOrderKey) return false;
    try {
      await getToken();
      const payload = {
        entity: {
          entity_id: Number(order.entity_id) || order.entity_id,
          status: 'processing',
          state: 'processing',
        },
      };

      const statusRes = await apiFetch('/rest/V1/orders', {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      if (!statusRes.ok) {
        const body = await statusRes.text();
        console.error('Release status update failed:', body || statusRes.status);
        return false;
      }

      try {
        await apiFetch(`/rest/V1/orders/${order.entity_id}/comments`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            statusHistory: {
              status: 'processing',
              comment: `Released back to processing by ${username}`,
              is_customer_notified: 0,
              is_visible_on_front: 0,
            },
          }),
        });
      } catch (commentErr) {
        console.warn('Release comment failed (ignored):', commentErr);
      }

      cleanupReleasedOrder(releasedOrderKey);

      setOrders((prev) => {
        const removedIndex = prev.findIndex((entry) => getOrderKey(entry) === releasedOrderKey);
        if (removedIndex === -1) return prev;
        const next = prev.filter((_, idx) => idx !== removedIndex);
        const nextIndex = next.length === 0 ? 0 : Math.min(removedIndex, next.length - 1);
        setCurrentIndex(nextIndex);
        setShowResolveAlert(false);
        return next;
      });

      return true;
    } catch (e) {
      console.error('Unexpected error while releasing order', releasedOrderKey, e);
      return false;
    }
  };

  const performReleaseOrders = async (ordersToRelease) => {
    if (!ordersToRelease || !ordersToRelease.length) return;
    setIsReleasing(true);
    setReleaseProgress({ current: 0, total: ordersToRelease.length });
    let successCount = 0;
    const failures = [];
    for (let i = 0; i < ordersToRelease.length; i += 1) {
      const ok = await releaseSingleOrder(ordersToRelease[i]);
      if (ok) successCount += 1;
      else failures.push(getOrderKey(ordersToRelease[i]));
      setReleaseProgress({ current: i + 1, total: ordersToRelease.length });
    }
    setIsReleasing(false);
    setShowReleaseConfirm(false);
    setReleaseMode(false);
    setReleaseSelection([]);
    if (successCount === 0 && failures.length) {
      alert(`Failed to release ${failures.length} order(s). Check console for details.`);
    } else if (failures.length) {
      alert(`Released ${successCount} order(s), but ${failures.length} failed. Check console for details.`);
    }
  };

  const handleReleaseOrder = async () => {
    if (!currentOrder) return;
    await performReleaseOrders([currentOrder]);
  };

  const handleReleaseSelected = async () => {
    const selectedOrders = orders.filter((o) => releaseSelection.includes(getOrderKey(o)));
    if (!selectedOrders.length) {
      alert('Select at least one order to release.');
      return;
    }
    const confirmMsg = `Release ${selectedOrders.length} selected order(s) back to processing?`;
    if (!window.confirm(confirmMsg)) return;
    await performReleaseOrders(selectedOrders);
  };

  const handleReleaseAll = async () => {
    if (!orders.length) return;
    const confirmMsg = `Release ALL ${orders.length} order(s) back to processing?`;
    if (!window.confirm(confirmMsg)) return;
    await performReleaseOrders(orders);
  };

  // Are all items marked Found or Remove?
  const allItemsResolved = (() => {
    const order = currentOrder;
    if (!order || !order.items || order.items.length === 0) return false;
    const picked = pickedItems[order.increment_id] || {};
    return order.items.every((it) => {
      const status = getItemStatus(order.increment_id, it.item_id);
      return status === 'found' || status === 'removed';
    });
  })();

  // Count unresolved items
  const unresolvedCount = (() => {
    const order = currentOrder;
    if (!order || !order.items || order.items.length === 0) return 0;
    return order.items.reduce((acc, it) => {
      const status = getItemStatus(order.increment_id, it.item_id);
      return acc + ((status === 'found' || status === 'removed') ? 0 : 1);
    }, 0);
  })();

  const sortedItems = React.useMemo(
    () => sortOrderItems(currentOrder?.items || []),
    [currentOrder?.items],
  );

  useEffect(() => {
    if (allItemsResolved && showResolveAlert) {
      setShowResolveAlert(false);
    }
  }, [allItemsResolved, showResolveAlert]);

  if (isLoading) {
    return (
      <div className="order-list-container picking-page">
        <LoadingOverlay show ariaLabel="Loading picking queue" />
      </div>
    );
  }

  return (
    <div className="order-list-container picking-page">
      <SkuBarcodeModal sku={skuCodeValue} onClose={() => setSkuCodeValue('')} />
      {orders.length === 0 ? (
        <div className="picking-empty-container">
          <div className="picking-empty">
            <h2>No orders in Picking</h2>
            <p>Select orders in the list page to begin the picking workflow.</p>
            <button
              type="button"
              className="back-button"
              onClick={() => navigate('/order-list')}
              style={{ marginTop: 20 }}
            >
              ← Back to Order List
            </button>
          </div>
        </div>
      ) : (
        <div className="picking-layout">
          <aside className="picking-sidebar">
            <div className="sidebar-header">
              <h3>Orders in Picking</h3>
              <span className="sidebar-count">{orders.length}</span>
            </div>
            <div className="sidebar-list">
              {orders.map((order, index) => {
                const { resolved, total } = getOrderProgress(order);
                const isActive = index === currentIndex;
                const customerFirst = order.customer_firstname || order?.billing_address?.firstname || '';
                const customerLast = order.customer_lastname || order?.billing_address?.lastname || '';
                const customerName = `${customerFirst} ${customerLast}`.trim() || 'Customer';
                const orderKey = getOrderKey(order);
                const isReleaseSelected = releaseSelection.includes(orderKey);
                const shippingLabel = (() => {
                  const raw = order.shipping_description || '';
                  const shipping = raw.toLowerCase();
                  if (shipping.includes('pickup')) return 'Click & Collect';
                  if (shipping.includes('dublin')) return 'Xpect';
                  return raw || 'N/A';
                })();
                return (
                  <button
                    key={order.entity_id || order.increment_id || index}
                    className={`sidebar-order ${isActive ? 'is-active' : ''}`}
                    onClick={() => handleSelectOrder(index)}
                  >
                    {releaseMode && (
                      <input
                        type="checkbox"
                        className="sidebar-release-checkbox"
                        checked={isReleaseSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          setReleaseSelection((prev) =>
                            prev.includes(orderKey)
                              ? prev.filter((id) => id !== orderKey)
                              : [...prev, orderKey]
                          );
                        }}
                      />
                    )}
                    <div className="sidebar-order-main">
                      <span className="order-id">#{order.increment_id}</span>
                      <span className="sidebar-order-ship">{shippingLabel}</span>
                      <span className="order-customer">{customerName}</span>
                    </div>
                    <div className="order-progress">
                      <span className="progress-dot" data-complete={resolved === total} />
                      <span>{resolved}/{total} done</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="picking-content">
            {showResolveAlert && (
              <div
                style={{
                  background: '#fff3cd',
                  border: '1px solid #ffeeba',
                  color: '#856404',
                  padding: '10px 12px',
                  borderRadius: 8,
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>Action required</span>
                  <span>
                    Resolve remaining items (mark as <strong>Found</strong> or <strong>Remove</strong>). Pending (including on hold): {unresolvedCount}
                  </span>
                </div>
                <button
                  aria-label="dismiss notice"
                  onClick={() => setShowResolveAlert(false)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 16,
                    lineHeight: 1,
                    color: '#856404'
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {isRefreshing && (
              <div className="picking-refresh-banner">
                Updating orders…
              </div>
            )}

            <div className="order-header">
              <div className="order-header-info">
                {!currentOrder?.items?.length && (
                  <p style={{ color: 'red' }}>⚠️ No items in this order.</p>
                )}
                <h2>Order #{currentOrder?.increment_id}</h2>
              </div>
              <div className="order-header-actions">
                <button
                  className="back-button action-button"
                  onClick={() => {
                    window.location.href = isOverrideView ? '/admin/operators' : '/order-list';
                  }}
                >
                  Back
                </button>
                <button
                  className="multi-picking-button action-button"
                  onClick={() => {
                    if (!currentOrder) return;
                    if (!allItemsResolved) {
                      setShowResolveAlert(true);
                      return;
                    }
                    savePickingQueueContext({ currentOrder, orders });
                    navigate(`/finalize/${currentOrder.increment_id}`);
                  }}
                  disabled={!currentOrder}
                  title={allItemsResolved ? '' : 'Resolve all items (Found or Remove) before proceeding'}
                >
                  Complete Order
                </button>
              </div>
            </div>

            {sortedItems.length > 0 ? (
              (() => {
                let hasShownFrozenDivider = false;
                let hasShownWholesaleDivider = false;
                return sortedItems.map((item) => {
                  const status = getItemStatus(currentOrder.increment_id, item.item_id);
                  const isFound = status === 'found';
                  const isRemoved = status === 'removed';
                  const isOnHold = status === 'hold';
                  const isFrozen = isFrozenItem(item);
                  const isWholesale = isWholesaleItem(item);
	                  const categoryInfo = getCategoryInfo(item);
	                  const orderedQty = Number(item.qty_ordered) || 0;
	                  const selectableQty = Math.max(Math.floor(orderedQty), 0);
	                  const snapshot = getEditSnapshot(currentOrder.increment_id, item.item_id, orderedQty);
                  const keepQty = snapshot.keepQty;
                  const removedQty = snapshot.removedQty;
                  const wasEdited = !isRemoved && !isOnHold && removedQty > 0 && keepQty !== orderedQty;

                  const isEditingCurrent =
                    editing &&
                    editing.orderId === currentOrder.increment_id &&
                    editing.itemId === item.item_id;
                  const menuOpen =
                    openActionMenu?.orderId === currentOrder.increment_id &&
                    openActionMenu?.itemId === item.item_id;

                  const itemClasses = ['order-item'];
                  if (isOnHold) itemClasses.push('is-on-hold');
                  if (isRemoved && !isOnHold) itemClasses.push('is-removed');
                  if (isFound && !isOnHold && !isRemoved) itemClasses.push('is-found');
                  if (wasEdited) itemClasses.push('is-edited');
                  const showFrozenDivider = isFrozen && !hasShownFrozenDivider;
                  const showWholesaleDivider = isWholesale && !hasShownWholesaleDivider;
                  if (showFrozenDivider) {
                    hasShownFrozenDivider = true;
                  }
                  if (showWholesaleDivider) {
                    hasShownWholesaleDivider = true;
                  }

                  const orderKey = currentOrder?.increment_id ? String(currentOrder.increment_id) : '';
                  const isCaseProduct = /case/i.test(item.name || '');
                  const locationVisible =
                    isCaseProduct ||
                    (orderKey &&
                      revealedLocations[orderKey] &&
                      revealedLocations[orderKey][String(item.item_id)]);
                  const storeKey = storeKeyFor(orderKey, item.item_id);
                  const revealLocation = () => {
                    if (!orderKey) return;
                    setRevealedLocations((prev) => {
                      const prevOrderMap = prev[orderKey] ? { ...prev[orderKey] } : {};
                      if (prevOrderMap[String(item.item_id)]) return prev;
                      const next = {
                        ...prev,
                        [orderKey]: {
                          ...prevOrderMap,
                          [String(item.item_id)]: true,
                        },
                      };
                      persistRevealedLocations(next);
                      return next;
                    });
                  };
                  return (
                    <React.Fragment key={item.item_id}>
                      {showFrozenDivider && (
                        <div className="item-group-divider">
                          {categoryInfo.label || 'Frozen items'}
                        </div>
                      )}
                      {showWholesaleDivider && (
                        <div className="item-group-divider">
                          {categoryInfo.label || 'Wholesale / Warehouse'}
                        </div>
                      )}
                      <div className={itemClasses.join(' ')}>
                        <img
                          src={item.image_url || 'https://via.placeholder.com/60'}
                          alt={item.name}
                          className="order-item-img"
                        />
                        <div className="order-details">
                          <p><strong>{item.name}</strong></p>
                          {(isOnHold || isRemoved) && (
                            <div className="item-flags">
                              {isOnHold && <span className="item-flag item-flag--hold">On Hold</span>}
                              {!isOnHold && isRemoved && (
                                <span className="item-flag item-flag--removed">Removed</span>
                              )}
                            </div>
                          )}
                          {wasEdited && !isOnHold && !isRemoved && (
                            <div className="item-flags">
                              <span className="item-flag item-flag--edited">Qty adjusted {orderedQty} → {keepQty}</span>
                            </div>
                          )}
                          <p className="item-qty">
                            {keepQty}
                            {' '}
                            {item.extension_attributes?.unit || 'pcs'}
                          </p>
                          <p className="item-store">
                            Lane:{' '}
                            {Object.prototype.hasOwnProperty.call(storeEditing, storeKey) ? (
                              <span className="store-edit-inline">
                                <input
                                  type="text"
                                  value={storeEditing[storeKey]}
                                  onChange={(e) => handleStoreInputChange(storeKey, e.target.value)}
                                  placeholder="Enter lane"
                                />
                                <button
                                  type="button"
                                  className="check-location-button"
                                  onClick={() => handleStoreSave(currentOrder, item, storeKey)}
                                  disabled={storeSavingKey === storeKey}
                                >
                                  {storeSavingKey === storeKey ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  className="check-location-button secondary"
                                  onClick={() => closeStoreEditing(storeKey)}
                                  disabled={storeSavingKey === storeKey}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <span className="store-location-display">
                                {item.extension_attributes?.lane || ''}
                              </span>
                            )}
                          </p>
                          {item.extension_attributes?.barcode && (
                            <p className="item-barcode">Barcode: <span>{item.extension_attributes.barcode}</span></p>
                          )}
                          {item.sku && (
                            <p className="item-barcode">
                              SKU:{' '}
                              <button
                                type="button"
                                className="sku-hold-button"
                                onPointerDown={() => startSkuHold(item.sku)}
                                onPointerUp={clearSkuHoldTimer}
                                onPointerCancel={clearSkuHoldTimer}
                                onPointerLeave={clearSkuHoldTimer}
                                onContextMenu={(event) => event.preventDefault()}
                                title="Hold to show scan code"
                              >
                                {item.sku}
                              </button>
                            </p>
                          )}
                        </div>
	                        <div className="order-actions">
	                          <button
	                            onClick={() => handleFoundClick(
	                              currentOrder.increment_id,
	                              item.item_id,
	                              orderedQty,
	                              isFound
	                            )}
	                            className={`picking-button action-button ${isFound ? 'found-active' : ''}`}
	                          >
	                            Found
                          </button>

                          {isEditingCurrent ? (
                            <div className="edit-qty-inline" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
	                              <select
	                                value={editing.value}
	                                onChange={(e) => setEditing((prev) => ({ ...prev, value: e.target.value }))}
	                              >
	                                {Array.from({ length: selectableQty }, (_, idx) => selectableQty - idx)
	                                  .map((q) => <option key={q} value={q}>{q}</option>)}
	                              </select>
                              <button className="picking-button action-button" onClick={() => saveEdit(currentOrder.increment_id, item.item_id)}>Save</button>
                              <button className="multi-picking-button action-button" onClick={cancelEdit}>Cancel</button>
                            </div>
                          ) : (
                            <div className="action-menu-wrapper">
                              <button
                                className="action-button action-menu-trigger"
                                onClick={() => toggleActionMenu(currentOrder.increment_id, item.item_id)}
                              >
                                More ▾
                              </button>
                              {menuOpen && (
                                <div className="action-menu-dropdown">
                                  <button
                                    className={`action-menu-option ${isRemoved ? 'is-active' : ''}`}
                                    onClick={() => toggleItemStatus(currentOrder.increment_id, item.item_id, 'removed')}
                                  >
                                    {isRemoved ? 'Undo Remove' : 'Remove item'}
                                  </button>
                                  <button
                                    className={`action-menu-option ${isOnHold ? 'is-active' : ''}`}
                                    onClick={() => toggleItemStatus(currentOrder.increment_id, item.item_id, 'hold')}
                                  >
                                    {isOnHold ? 'Clear On Hold' : 'Mark as On Hold'}
                                  </button>
                                  <button
                                    className="action-menu-option"
                                    onClick={() => startEdit(
                                      currentOrder.increment_id,
                                      item.item_id,
                                      orderedQty
                                    )}
                                  >
                                    Adjust quantity…
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                });
              })()
            ) : (
              <p>No items available.</p>
            )}

            <div className="bottom-nav">
              <div className="nav-left">
                <button
                  onClick={() => setCurrentIndex(prev => Math.max(prev - 1, 0))}
                  className="picking-button action-button"
                  disabled={currentIndex === 0}
                >
                  Previous
                </button>
              </div>
              <div className="nav-center">
                {!releaseMode ? (
                  <button
                    className="multi-picking-button action-button"
                    onClick={enableReleaseMode}
                    disabled={isReleasing || orders.length === 0}
                  >
                    Release orders
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                      <button
                        className="multi-picking-button action-button"
                        onClick={handleReleaseSelected}
                        disabled={isReleasing || releaseSelection.length === 0}
                        title="Release selected orders from the list"
                      >
                        {isReleasing ? 'Releasing…' : `Release Selected (${releaseSelection.length})`}
                      </button>
                      <button
                        className="multi-picking-button action-button"
                        onClick={handleReleaseAll}
                        disabled={isReleasing || orders.length === 0}
                        title="Release all orders in the current picking queue"
                      >
                        {isReleasing ? 'Releasing…' : `Release All (${orders.length})`}
                      </button>
                      <button
                        className="multi-picking-button action-button secondary"
                        onClick={cancelReleaseMode}
                        disabled={isReleasing}
                      >
                        Cancel
                      </button>
                    </div>
                    {(isReleasing && releaseProgress.total > 1) && (
                      <div className="picking-progress-text" style={{ marginTop: 4 }}>
                        {releaseProgress.current}/{releaseProgress.total} released…
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="nav-right">
                <button
                  onClick={() => setCurrentIndex(prev => Math.min(prev + 1, orders.length - 1))}
                  className="picking-button action-button"
                  disabled={currentIndex === orders.length - 1}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={showReleaseConfirm}
        title={`Release Order #${currentOrder?.increment_id || ''}`}
        message="Are you sure you want to release this order back to the queue?"
        confirmLabel="Yes, release"
        cancelLabel="Cancel"
        onConfirm={handleReleaseOrder}
        onCancel={() => setShowReleaseConfirm(false)}
      />
    </div>
  );
};

export default PickingPage;
